/**
 * WordPress dependencies
 */
import { useSelect, useDispatch } from '@wordpress/data';
import { createRoot, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { store as addToCartStore } from '../../data/add-to-cart';
import { tracker } from '../../services/analytics';

import { AddToCartButton } from './components/AddToCartButton';
import { PricingSummary } from './components/PricingSummary';
import { QuantitySelector } from './components/QuantitySelector';
import { VariantSelector } from './components/VariantSelector';

type AddToCartBlockProps = {
	readonly currency: string;
	readonly formattedPrice: string;
	readonly price: number;
	readonly productId: number;
	readonly productName: string;
	readonly productType: string;
};

const addToCartErrorFallback = ( error: Error ) => (
	<div className="ebs-add-to-cart ebs-add-to-cart--error" role="alert">
		<h3>Unable to load booking form</h3>
		<p>{ error.message }</p>
		<button
			type="button"
			className="ebs-add-to-cart__button"
			onClick={ () => { globalThis.location.reload(); } }
		>
			Reload Page
		</button>
	</div>
);

const AddToCartBlockContent = ( {
	productId,
	productName,
	price,
	currency,
	formattedPrice,
	productType,
}: AddToCartBlockProps ) => {
	const dispatch = useDispatch( addToCartStore );

	const { instance, selectedVariant, successMessage } = useSelect(
		( select ) => {
			const s = select( addToCartStore );
			const inst = s.getInstance( productId );
			return {
				instance: inst,
				selectedVariant: s.getSelectedVariant( productId ),
				successMessage: s.getSuccessMessage( productId ),
			};
		},
		[ productId ],
	);

	const isInitialized = instance?.isInitialized ?? false;
	const isVariableProduct = productType === 'variable';

	// Track view_item once after the block finishes initializing.
	const hasTrackedView = useRef( false );
	useEffect( () => {
		if ( ! isInitialized || hasTrackedView.current ) return;
		hasTrackedView.current = true;
		tracker.viewItem( currency, [ {
			item_id: String( productId ),
			item_name: productName,
			price: price / 100,
			quantity: 1,
		} ] );
	}, [ isInitialized, productId, productName, price, currency ] );

	// Track select_item whenever the selected variant changes.
	const prevVariantId = useRef< null | number >( null );
	useEffect( () => {
		if ( ! selectedVariant ) return;
		if ( selectedVariant.id === prevVariantId.current ) return;
		prevVariantId.current = selectedVariant.id;
		tracker.selectItem( currency, [ {
			item_id: String( productId ),
			item_name: productName,
			item_variant: selectedVariant.name,
			price: selectedVariant.price_amount / 100,
			quantity: instance?.quantity ?? 1,
		} ] );
	}, [ selectedVariant, productId, productName, currency, instance?.quantity ] );

	// Auto-dismiss success message after 4 seconds
	useEffect( () => {
		if ( ! successMessage ) return;
		const timer = setTimeout( () => {
			void dispatch.setSuccess( productId, null );
		}, 4000 );
		return () => { clearTimeout( timer ); };
	}, [ successMessage, productId, dispatch ] );

	// Initialize on mount
	useEffect( () => {
		if ( ! isInitialized ) {
			void dispatch.initializeProduct( productId, price, currency, formattedPrice, productType );
		}
	}, [ productId, price, currency, formattedPrice, productType, isInitialized, dispatch ] );

	if ( ! isInitialized ) {
		return <div className="ebs-add-to-cart-loading">Loading product...</div>;
	}

	return (
		<div className="ebs-add-to-cart">
			{ successMessage ? (
				<div className="ebs-add-to-cart__success" role="status">
					<span className="ebs-add-to-cart__success-icon">✓</span>
					<span>{ successMessage }</span>
					<button
						type="button"
						className="ebs-add-to-cart__success-dismiss"
						onClick={ () => { void dispatch.setSuccess( productId, null ); } }
						aria-label="Dismiss"
					>
						×
					</button>
				</div>
			) : null }
			{ isVariableProduct ? <VariantSelector productId={ productId } /> : null }
			<PricingSummary productId={ productId } />
			<div className="ebs-add-to-cart__bottom-row">
				<QuantitySelector productId={ productId } />
				<AddToCartButton productId={ productId } productType={ productType } />
			</div>
		</div>
	);
};

const AddToCartBlock = ( props: AddToCartBlockProps ) => (
	<ErrorBoundary fallback={ addToCartErrorFallback }>
		<AddToCartBlockContent { ...props } />
	</ErrorBoundary>
);


function initBlocks() {
	const containers = document.querySelectorAll( '.wp-block-ebs-add-to-cart' );

	for ( const element of containers ) {
		const container = element as HTMLElement;
		const productId = Number.parseInt( container.dataset.productId ?? '0', 10 );
		const productName = container.dataset.productName ?? '';
		const price = Number.parseInt( container.dataset.price ?? '0', 10 );
		const currency = container.dataset.currency ?? 'USD';
		const formattedPrice = container.dataset.formattedPrice ?? '$0.00';
		const productType = container.dataset.productType ?? 'simple';

		if ( productId > 0 ) {
			const root = createRoot( container );
			root.render(
				<AddToCartBlock
					productId={ productId }
					productName={ productName }
					price={ price }
					currency={ currency }
					formattedPrice={ formattedPrice }
					productType={ productType }
				/>,
			);
		}
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initBlocks );
} else {
	initBlocks();
}
